import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Cancel,
} from '@mui/icons-material';
import { uisTrackerAPI } from '../../services/uisTrackerApi';

const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  reportable: { color: '#4caf50', icon: <CheckCircle sx={{ color: '#4caf50' }} />, label: 'Reportable' },
  partial: { color: '#ff9800', icon: <Warning sx={{ color: '#ff9800' }} />, label: 'Partial Data' },
  not_reportable: { color: '#f44336', icon: <Cancel sx={{ color: '#f44336' }} />, label: 'Not Reportable' },
};

const IndicatorCard: React.FC<{ indicator: any }> = ({ indicator }) => {
  const config = statusConfig[indicator.status] || statusConfig.not_reportable;
  return (
    <Paper sx={{ p: 2, borderLeft: `4px solid ${config.color}`, height: '100%' }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box display="flex" alignItems="center" gap={1}>
          {config.icon}
          <Typography variant="subtitle2" fontWeight={600}>{indicator.name}</Typography>
        </Box>
        <Chip label={config.label} size="small" sx={{ bgcolor: config.color, color: 'white', fontSize: '0.65rem' }} />
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
        {indicator.description}
      </Typography>
      <Chip label={indicator.category} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
      {indicator.issue && (
        <Typography variant="caption" color="error" display="block" mt={1}>
          {indicator.issue}
        </Typography>
      )}
      {indicator.data_checks && Object.entries(indicator.data_checks).map(([key, value]) => (
        key !== 'note' && (
          <Typography key={key} variant="caption" color="text.secondary" display="block">
            {key.replace(/_/g, ' ')}: <strong>{typeof value === 'number' ? (key.includes('coverage') ? `${value}%` : value.toLocaleString()) : String(value)}</strong>
          </Typography>
        )
      ))}
    </Paper>
  );
};

const getCompletenessColor = (pct: number) => {
  if (pct >= 80) return '#4caf50';
  if (pct >= 60) return '#ff9800';
  return '#f44336';
};

const UISTrackerDashboard: React.FC = () => {
  const [tab, setTab] = React.useState(0);

  const { data: indicatorData, isLoading: loadingIndicators } = useQuery({
    queryKey: ['uis-indicator-status'],
    queryFn: () => uisTrackerAPI.getIndicatorStatus(),
    select: (res) => res.data,
  });

  const { data: completenessData, isLoading: loadingCompleteness } = useQuery({
    queryKey: ['uis-completeness'],
    queryFn: () => uisTrackerAPI.getCompleteness(),
    select: (res) => res.data,
  });

  if (loadingIndicators || loadingCompleteness) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const reportable = indicatorData?.reportable_count || 0;
  const total = indicatorData?.total_indicators || 12;

  return (
    <Box p={3}>
      <Typography variant="h4" fontWeight={700} mb={0.5}>
        UIS Indicator Tracker
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Track progress toward full UIS indicator reporting coverage (GPE 2025 Results Framework)
      </Typography>

      {/* Summary */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Box position="relative" display="inline-flex" mb={2}>
              <CircularProgress
                variant="determinate"
                value={(reportable / total) * 100}
                size={120}
                thickness={6}
                sx={{ color: reportable >= 10 ? '#4caf50' : reportable >= 8 ? '#ff9800' : '#f44336' }}
              />
              <Box position="absolute" top={0} left={0} bottom={0} right={0} display="flex" alignItems="center" justifyContent="center">
                <Typography variant="h4" fontWeight={700}>{reportable}/{total}</Typography>
              </Box>
            </Box>
            <Typography variant="h6" fontWeight={600}>UIS Indicators Reportable</Typography>
            <Typography variant="body2" color="text.secondary">
              Baseline: 8/12 | Target: 12/12
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                <Typography variant="h3" fontWeight={700} color="#4caf50">{indicatorData?.reportable_count || 0}</Typography>
                <Typography variant="body2">Reportable</Typography>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
                <Typography variant="h3" fontWeight={700} color="#ff9800">{indicatorData?.partial_count || 0}</Typography>
                <Typography variant="body2">Partial Data</Typography>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee' }}>
                <Typography variant="h3" fontWeight={700} color="#f44336">{indicatorData?.not_reportable_count || 0}</Typography>
                <Typography variant="body2">Not Reportable</Typography>
              </Paper>
            </Grid>
            {indicatorData?.by_category && Object.entries(indicatorData.by_category).map(([cat, vals]: [string, any]) => (
              <Grid item xs={3} key={cat}>
                <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>{cat}</Typography>
                  <Typography variant="caption" color="text.secondary">{vals.reportable}/{vals.total}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Indicator Status" />
        <Tab label="Data Completeness by District" />
      </Tabs>

      {/* Tab 0: Indicators */}
      {tab === 0 && (
        <Grid container spacing={2}>
          {indicatorData?.indicators?.map((ind: any) => (
            <Grid item xs={12} sm={6} md={4} key={ind.id}>
              <IndicatorCard indicator={ind} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Tab 1: Completeness */}
      {tab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>Data Completeness by District</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>District</strong></TableCell>
                  <TableCell><strong>Zone</strong></TableCell>
                  <TableCell align="right"><strong>DOB Coverage</strong></TableCell>
                  <TableCell align="right"><strong>Gender Coverage</strong></TableCell>
                  <TableCell align="right"><strong>Staff Qualification</strong></TableCell>
                  <TableCell align="right"><strong>Overall</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {completenessData?.districts?.map((d: any) => (
                  <TableRow key={d.district}>
                    <TableCell>{d.district}</TableCell>
                    <TableCell>
                      <Chip label={d.zone} size="small" color={d.zone === 'Pemba' ? 'warning' : 'info'} variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                        <LinearProgress
                          variant="determinate"
                          value={d.dob_coverage}
                          sx={{ width: 60, height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: getCompletenessColor(d.dob_coverage) } }}
                        />
                        <Typography variant="caption" fontWeight={600}>{d.dob_coverage}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                        <LinearProgress
                          variant="determinate"
                          value={d.gender_coverage}
                          sx={{ width: 60, height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: getCompletenessColor(d.gender_coverage) } }}
                        />
                        <Typography variant="caption" fontWeight={600}>{d.gender_coverage}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                        <LinearProgress
                          variant="determinate"
                          value={d.staff_qualification_coverage}
                          sx={{ width: 60, height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: getCompletenessColor(d.staff_qualification_coverage) } }}
                        />
                        <Typography variant="caption" fontWeight={600}>{d.staff_qualification_coverage}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${d.overall_completeness}%`}
                        size="small"
                        sx={{ bgcolor: getCompletenessColor(d.overall_completeness), color: 'white', fontWeight: 600 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default UISTrackerDashboard;
